import z from "zod";

/**
 * Ad-attribution identifiers captured in the browser at form submission.
 *
 * Mirrors functions/src/core/dtos/tracking/tracking-schema.ts — keep the two in
 * step. Every field is optional: what gets captured depends on how the visitor
 * arrived and whether the Meta/GA pixels were allowed to run.
 */
export const trackingDataSchema = z.object({
  client: z
    .object({
      ipAddress: z.string().optional(),
      userAgent: z.string().optional(),
      eventSourceUrl: z.string().optional(),
    })
    .optional(),

  // Recorded on the page the visitor arrived on, not the page they submitted
  // from — see client.eventSourceUrl for the latter.
  campaign: z
    .object({
      utmCampaign: z.string().optional(),
      utmTerm: z.string().optional(),
      landingPageUrl: z.string().optional(),
    })
    .optional(),

  meta: z
    .object({
      fbp: z.string().optional(),
      fbc: z.string().optional(),
    })
    .optional(),

  googleAds: z
    .object({
      gclid: z.string().optional(),
      wbraid: z.string().optional(),
      gbraid: z.string().optional(),
    })
    .optional(),

  ga4: z
    .object({
      clientId: z.string().optional(),
      sessionId: z.string().optional(),
    })
    .optional(),

  privacy: z
    .object({
      // Tri-state on purpose: null means the visitor never answered the cookie
      // banner, which is different from an explicit refusal.
      adUserData: z.boolean().nullable().optional(),
      adPersonalization: z.boolean().nullable().optional(),
      country: z.string().optional(),
      region: z.string().optional(),
      inEu: z.boolean().optional(),
    })
    .optional(),
});

export type TrackingData = z.infer<typeof trackingDataSchema>;
